// export default AssetRegister;
import { useTooltip } from "context/TooltipProvider";
import React, { useEffect, useState } from "react";
import {
  createAsset,
  fetchAssetCaseConditionsList,
  fetchAssetCrimeCategoriesList,
  fetchAssetOwnerRelationshipsList,
} from "services/assetRecoveryService";
import { Asset, Referral } from "types";
import { errorHandlerOnForm } from "utils/helper";
import Select, { MultiValue } from "react-select";
interface OwnerOption {
  value: string | number; // depending on your backend
  label: string;
}

const defaultFormData: Asset = {
  asset_condition: "",
  court_verdict_decision_type: "",
  asset_owned_date: "",
  asset_location_region: "",
  asset_location_zone: "",
  asset_location_woreda: "",
  asset_location_town: "",
  asset_category: "",
  asset_owner: [],
  asset_number: "",
  estimation_of_money: "",
  quantity: "",
  is_owner_above_18: false,
  is_the_suspect_the_owner: false,
  case_condition: "",
  crime_category: "",
  crime_location: "",
  how_asset_acquired: "",
  has_asset_recovery_decision: false,
  asset_unique_id: "",
  owner: "",
  owner_relationship_with_suspect: "",
  spouse: "",
  referral: "",
  crime_location_region: "",
  crime_location_town: "",
  crime_location_woreda: "",
  crime_house_number: "",
  crime_location_zone: "",
  crime_reference: "",
};

const AssetRegister: React.FC<{
  referralId: string | undefined;
  referral: Referral | null;
  setActiveTab?: React.Dispatch<React.SetStateAction<string>>;
  setShowAddAssetForm?: React.Dispatch<React.SetStateAction<boolean>>;
  onAssetCreated?: (newAsset: Asset) => void;
}> = ({
  referralId,
  referral,
  setActiveTab,
  setShowAddAssetForm,
  onAssetCreated,
}: {
  referralId: string | undefined;
  referral: Referral | null;
  setActiveTab?: React.Dispatch<React.SetStateAction<string>>;
  setShowAddAssetForm?: React.Dispatch<React.SetStateAction<boolean>>;
  onAssetCreated?: (newAsset: Asset) => void;
}) => {
  const { showTooltip } = useTooltip();
  const [formState, setFormState] = useState<Asset>(defaultFormData);
  const [ownerRelationships, setOwnerRelationships] = useState<
    { uu_id: string; name: string }[]
  >([]);
  const [assetCrimeCategories, setAssetCrimeCategories] = useState<
    { uu_id: string; name: string }[]
  >([]);
  const [caseConditions, setAssetCaseCondition] = useState<
    { uu_id: string; name: string }[]
  >([]);

  const handleChange = (
    e:
      | React.ChangeEvent<
          HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
        >
      | { target: { name: string; value: string | string[] | boolean } },
  ) => {
    const { name, value } = e.target;

    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const assetConditions = [
    { uu_id: "on_prohibition", name: "On Prohibition" },
    {
      uu_id: "no_longer_under_prohibition",
      name: "No longer Under Prohibition",
    },
    { uu_id: "not_prohibited", name: "Not Prohibited" },
  ];
  const courtVerdictChoices = [
    { uu_id: "guilty", name: "Guilty" },
    { uu_id: "not_guilty", name: "Not Guilty" },
  ];
  const validateForm = () => {
    if (!formState.asset_number) return "Asset Number is required.";
    if (!formState.asset_category) return "Asset category is required.";
    if (!formState.asset_condition) return "Asset condition is required.";
    if (!formState.crime_category) return "Crime category is required.";
    if (!formState.asset_owned_date) return "Asset owned date is required.";

    if (!formState.case_condition) return "Case condition is required.";
    if (!formState.court_verdict_decision_type)
      return "Court verdict decision type is required.";
    if (!formState.owner_relationship_with_suspect)
      return "Owner relationship with suspect is required.";

    return "";
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const error = validateForm();

    if (error) {
      showTooltip(error, "error");
      return;
    }

    try {
      if (referralId) {
        formState.referral = referralId;
      }
      createAsset(formState)
        .then((response) => {
          setFormState(defaultFormData);
          showTooltip("Successfully Submitted!", "success");
          setShowAddAssetForm?.(false);
          if (onAssetCreated && response) {
            onAssetCreated(response);
          }
          setActiveTab?.("Assets");
        })
        .catch((error) => {
          showTooltip(
            errorHandlerOnForm(
              (error as { message: string }) || "Failed to create asset",
            ),
          );
        });
    } catch (error) {
      showTooltip(
        errorHandlerOnForm(error as { message: string }) ||
          "Failed to create asset",
        "error",
      );
    }
  };

  useEffect(() => {
    fetchAssetOwnerRelationshipsList()
      .then(setOwnerRelationships)
      .catch(console.error);
    fetchAssetCrimeCategoriesList()
      .then(setAssetCrimeCategories)
      .catch(console.error);
    fetchAssetCaseConditionsList()
      .then(setAssetCaseCondition)
      .catch(console.error);
  }, []);

  return (
    <div className="max-w-screen-xl ml-1 mr-4 space-y-6">
      <form onSubmit={handleSubmit}>
        <div className="bg-white  p-6 mt-4">
          <div>
            {/* Basic Asset Information */}
            <div
              className={`${
                (referral?.owners?.length ?? 0) > 0
                  ? "grid-cols-3"
                  : "grid-cols-2"
              } grid gap-4 mt-4`}
            >
              <div className="flex flex-col">
                <label className="referral-form-label">Asset Condition</label>
                <select
                  className="referral-form-field"
                  name="asset_condition"
                  value={formState.asset_condition}
                  onChange={handleChange}
                >
                  <option value="">Select Asset Condition</option>
                  {assetConditions.map((cond) => (
                    <option key={cond.uu_id} value={cond.uu_id}>
                      {cond.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col">
                <label className="referral-form-label">
                  Court Verdict Decision
                </label>
                <select
                  name="court_verdict_decision_type"
                  className="referral-form-field"
                  value={formState.court_verdict_decision_type}
                  onChange={handleChange}
                >
                  <option value="" className="text-gray-500">
                    Select Court Verdict Decision
                  </option>

                  {courtVerdictChoices?.map((decision) => (
                    <option key={decision.uu_id} value={decision.uu_id}>
                      {decision.name}
                    </option>
                  ))}
                </select>
              </div>
              {referral?.owners && referral.owners.length > 0 && (
                <div className="flex flex-col">
                  <label className="referral-form-label">Asset Owner</label>
                  <Select<OwnerOption, true> // 👈 true means multi-select
                    className="asset-form-field p-5"
                    classNamePrefix="rs"
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        border: "none",
                        padding: 0,
                        background: "transparent",
                        margin: "0",
                        color: "black",
                      }),
                      option: (provided, state) => ({
                        ...provided,
                        backgroundColor: state.isSelected
                          ? "transparent"
                          : state.isFocused
                            ? "transparent"
                            : "white",
                        color: state.isSelected ? "white" : "#212121",
                        fontSize: "14px",
                      }),
                    }}
                    isMulti
                    options={referral.owners.map((owner) => ({
                      value: owner.id,
                      label: owner.person.full_name,
                    }))}
                    value={
                      formState.asset_owner
                        ? referral.owners
                            .filter((owner) =>
                              formState.asset_owner.includes(owner.id),
                            )
                            .map((owner) => ({
                              value: owner.id,
                              label: owner.person.full_name,
                            }))
                        : []
                    }
                    onChange={(selectedOptions: MultiValue<OwnerOption>) => {
                      const selectedValues = selectedOptions.map(
                        (option) => option.value,
                      );
                      handleChange({
                        target: {
                          name: "asset_owner",
                          value: selectedValues as string[],
                        },
                      });
                    }}
                    placeholder="Select Asset Owners"
                  />
                </div>
              )}
            </div>
            {formState.asset_condition === "on_prohibition" && (
              <div className="mt-6 p-4 border border-gray-200 rounded-lg">
                <h3 className="text-lg font-semibold mb-4 text-[#9D0303]">
                  Prohibition Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col">
                    <label className="referral-form-label">Court Name</label>
                    <input
                      type="text"
                      className="referral-form-field"
                      name="court_name"
                      value={formState.court_name}
                      onChange={handleChange}
                      placeholder="Enter court name"
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="referral-form-label">
                      Court Registration Number
                    </label>
                    <input
                      type="text"
                      className="referral-form-field"
                      name="court_registration_number"
                      value={formState.court_registration_number}
                      onChange={handleChange}
                      placeholder="Enter registration number"
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="referral-form-label">
                      Date of Prohibition
                    </label>
                    <input
                      type="date"
                      className="referral-form-field"
                      name="date_of_prohibition"
                      value={formState.date_of_prohibition}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="referral-form-label">
                      Prohibition Delivery Evidence
                    </label>
                    <input
                      type="text"
                      className="referral-form-field"
                      name="prohibition_delivery_evidence"
                      value={formState.prohibition_delivery_evidence}
                      onChange={handleChange}
                      placeholder="Enter evidence details"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* No Longer Under Prohibition Fields */}
            {formState.asset_condition === "no_longer_under_prohibition" && (
              <div className="mt-6 p-4 border border-gray-200 rounded-lg">
                <h3 className="text-lg font-semibold mb-4 text-[#9D0303]">
                  Prohibition Removal Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col">
                    <label className="referral-form-label">
                      Prohibition Removal Order
                    </label>
                    <input
                      type="text"
                      className="referral-form-field"
                      name="prohibition_removal_order"
                      value={formState.prohibition_removal_order}
                      onChange={handleChange}
                      placeholder="Enter removal order"
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="referral-form-label">Court Name</label>
                    <input
                      type="text"
                      className="referral-form-field"
                      name="court_name_for_removal"
                      value={formState.court_name_for_removal}
                      onChange={handleChange}
                      placeholder="Enter court name"
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="referral-form-label">Court Level</label>
                    <input
                      type="text"
                      className="referral-form-field"
                      name="court_level_for_removal"
                      value={formState.court_level_for_removal}
                      onChange={handleChange}
                      placeholder="Enter court level"
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="referral-form-label">Court Region</label>
                    <input
                      type="text"
                      className="referral-form-field"
                      name="court_region_for_removal"
                      value={formState.court_region_for_removal}
                      onChange={handleChange}
                      placeholder="Enter court region"
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="referral-form-label">Court Zone</label>
                    <input
                      type="text"
                      className="referral-form-field"
                      name="court_zone_for_removal"
                      value={formState.court_zone_for_removal}
                      onChange={handleChange}
                      placeholder="Enter court zone"
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="referral-form-label">Court Woreda</label>
                    <input
                      type="text"
                      className="referral-form-field"
                      name="court_woreda_for_removal"
                      value={formState.court_woreda_for_removal}
                      onChange={handleChange}
                      placeholder="Enter court woreda"
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="referral-form-label">
                      Date of Prohibition Removal
                    </label>
                    <input
                      type="date"
                      className="referral-form-field"
                      name="date_of_prohibition_removal"
                      value={formState.date_of_prohibition_removal}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="referral-form-label">
                      Prohibition Removal Reason
                    </label>
                    <textarea
                      className="referral-form-field h-20"
                      name="prohibition_removal_reason"
                      value={formState.prohibition_removal_reason}
                      onChange={handleChange}
                      placeholder="Enter reason for removal"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Not Prohibited Fields */}
            {formState.asset_condition === "not_prohibited" && (
              <div className="mt-6 p-4 border border-gray-200 rounded-lg">
                <h3 className="text-lg font-semibold mb-4 text-[#9D0303]">
                  Not Prohibited Information
                </h3>
                <div className="flex flex-col">
                  <label className="referral-form-label">
                    Reason for Not Prohibition
                  </label>
                  <textarea
                    className="referral-form-field h-24"
                    name="reason_for_not_prohibition"
                    value={formState.reason_for_not_prohibition}
                    onChange={handleChange}
                    placeholder="Enter reason why asset is not prohibited"
                  />
                </div>
              </div>
            )}
            <div>
              {" "}
              {/* Asset Location */}
              <div className="flex items-center mt-4 w-[678px]">
                <p className="text-[#9D0303] font-[Poppins] font-[600] text-lg cursor-pointer">
                  Asset Location
                </p>
                <hr className="flex-1 border-t border-[#E9E9E9] ml-4" />
              </div>
              {/* <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-4">
                <div className="flex w-full">
                  {" "}
                  <div className="flex flex-col mr-5 w-full">
                    <label className="referral-form-label">Region</label>
                    <input
                      type="text"
                      className="referral-form-field"
                      name="asset_location_region"
                      value={formState.asset_location_region}
                      onChange={handleChange}
                      placeholder="Enter region"
                    />
                  </div>
                  <div className="flex flex-col w-full">
                    <label className="referral-form-label">Zone</label>
                    <input
                      type="text"
                      className="referral-form-field"
                      name="asset_location_zone"
                      value={formState.asset_location_zone}
                      onChange={handleChange}
                      placeholder="Enter zone"
                    />
                  </div>
                </div>

                <div className="flex w-full">
                  <div className="flex flex-col mr-5 w-full">
                    <label className="referral-form-label">Woreda</label>
                    <input
                      type="text"
                      className="referral-form-field"
                      name="asset_location_woreda"
                      value={formState.asset_location_woreda}
                      onChange={handleChange}
                      placeholder="Enter woreda"
                    />
                  </div>

                  <div className="flex flex-col w-full">
                    <label className="referral-form-label">Kebele</label>
                    <input
                      type="text"
                      className="referral-form-field"
                      name="asset_location_town"
                      value={formState.asset_location_town}
                      onChange={handleChange}
                      placeholder="Enter kebele"
                    />
                  </div>
                </div>
              </div> */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-4">
                {/* Row 1 */}
                <div className="flex flex-col w-full">
                  <label className="referral-form-label">Region</label>
                  <input
                    type="text"
                    className="referral-form-field"
                    name="asset_location_region"
                    value={formState.asset_location_region}
                    onChange={handleChange}
                    placeholder="Enter region"
                  />
                </div>

                <div className="flex flex-col w-full">
                  <label className="referral-form-label">Zone</label>
                  <input
                    type="text"
                    className="referral-form-field"
                    name="asset_location_zone"
                    value={formState.asset_location_zone}
                    onChange={handleChange}
                    placeholder="Enter zone"
                  />
                </div>

                {/* Row 2 */}
                <div className="flex flex-col w-full">
                  <label className="referral-form-label">Woreda</label>
                  <input
                    type="text"
                    className="referral-form-field"
                    name="asset_location_woreda"
                    value={formState.asset_location_woreda}
                    onChange={handleChange}
                    placeholder="Enter woreda"
                  />
                </div>

                <div className="flex flex-col w-full">
                  <label className="referral-form-label">Kebele</label>
                  <input
                    type="text"
                    className="referral-form-field"
                    name="asset_location_town"
                    value={formState.asset_location_town}
                    onChange={handleChange}
                    placeholder="Enter kebele"
                  />
                </div>
              </div>
            </div>
            <div className="flex items-center mt-4 w-[678px]">
              <p className="text-[#9D0303] font-[Poppins] font-[600] text-lg cursor-pointer">
                Asset Information
              </p>
              <hr className="flex-1 border-t border-[#E9E9E9] ml-4" />
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="flex flex-col">
                <label className="referral-form-label">Asset Category</label>
                <input
                  type="text"
                  name="asset_category"
                  value={formState.asset_category}
                  onChange={handleChange}
                  className="referral-form-field"
                  placeholder="Enter category"
                />
              </div>

              <div className="flex flex-col">
                <label className="referral-form-label">
                  Asset Identification Number
                </label>
                <input
                  type="text"
                  className="referral-form-field"
                  name="asset_number"
                  value={formState.asset_number}
                  onChange={handleChange}
                  placeholder="Enter asset identification number"
                />
              </div>
            </div>
            {/* Asset Details */}
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="flex flex-col">
                <label className="referral-form-label">Asset Owned Date</label>
                <input
                  type="date"
                  className="referral-form-field"
                  name="asset_owned_date"
                  value={formState.asset_owned_date}
                  onChange={handleChange}
                  placeholder="Enter asset owned date"
                />
              </div>

              <div className="flex flex-col">
                <label className="referral-form-label">
                  Estimated Money(ETB)
                </label>
                <input
                  type="text"
                  className="referral-form-field"
                  name="estimation_of_money"
                  value={formState.estimation_of_money}
                  onChange={handleChange}
                  placeholder="Enter estimation money"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="flex items-center asset-checkbox-container">
                <label className="asset-checkbox-label">
                  Is owner age above 18?
                </label>

                <input
                  type="checkbox"
                  name="is_owner_above_18"
                  checked={formState.is_owner_above_18}
                  onChange={handleChange}
                  className="mr-2 asset-checkbox"
                />
              </div>

              <div className="flex flex-col">
                <label className="referral-form-label">Quantity</label>
                <input
                  type="text"
                  className="referral-form-field"
                  name="quantity"
                  value={formState.quantity}
                  onChange={handleChange}
                  placeholder="Enter quantity"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="flex items-center asset-checkbox-container">
                <label className="asset-checkbox-label">
                  Is the Suspect the Owner of the asset?
                </label>
                <input
                  type="checkbox"
                  name="is_the_suspect_the_owner"
                  checked={formState.is_the_suspect_the_owner}
                  onChange={handleChange}
                  className="mr-2 asset-checkbox"
                />
              </div>

              <div className="flex flex-col">
                <label className="referral-form-label">
                  Suspect relation with the owner
                </label>
                <select
                  className="referral-form-field"
                  name="owner_relationship_with_suspect"
                  value={formState.owner_relationship_with_suspect}
                  onChange={handleChange}
                >
                  <option value="">Select Suspect Relation</option>
                  {ownerRelationships.map((relationship) => (
                    <option key={relationship.uu_id} value={relationship.uu_id}>
                      {relationship.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex flex-col">
              <label className="referral-form-label">
                How Asset Acquired Narration
              </label>
              <textarea
                className="referral-form-field h-24"
                name="how_asset_acquired"
                value={formState.how_asset_acquired}
                onChange={handleChange}
              />
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="flex items-center asset-checkbox-container mt-4">
                <label className="asset-checkbox-label">
                  Has Asset Recovery Decision
                </label>
                <input
                  type="checkbox"
                  name="has_asset_recovery_decision"
                  checked={formState.has_asset_recovery_decision}
                  onChange={handleChange}
                  className="mr-2 asset-checkbox"
                />
              </div>
              <div className="flex flex-col">
                <label className="referral-form-label">Case Condition</label>
                <select
                  className="referral-form-field"
                  name="case_condition"
                  value={formState.case_condition}
                  onChange={handleChange}
                >
                  <option value="">Select Case Condition</option>

                  {caseConditions?.map((condition) => (
                    <option key={condition?.uu_id} value={condition?.uu_id}>
                      {condition?.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {/* Crime Information */}
            <div className="flex items-center mt-4 w-[678px]">
              <p className="text-[#9D0303] font-[Poppins] font-[600] text-lg cursor-pointer">
                Crime Information
              </p>
              <hr className="flex-1 border-t border-[#E9E9E9] ml-4" />
            </div>
            <div className="grid grid-cols-3 gap-2 mt-4">
              <div>
                <div className="flex flex-col">
                  <label className="referral-form-label">Crime Category</label>
                  <select
                    className="referral-form-field"
                    name="crime_category"
                    value={formState.crime_category}
                    onChange={handleChange}
                  >
                    <option value="">Select Crime Category</option>
                    {assetCrimeCategories.map((category) => (
                      <option key={category.uu_id} value={category.uu_id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {formState?.crime_category?.toLowerCase() === "others" && (
                <div>
                  <label className="referral-form-label">
                    if Crime Category is Others
                  </label>
                  <input
                    type="text"
                    className="referral-form-field"
                    name="crime_category_others"
                    value={formState.crime_category_others}
                    onChange={handleChange}
                    placeholder="Enter how asset was acquired"
                  />
                </div>
              )}

              <div className="flex flex-col">
                <label className="referral-form-label">Crime Reference</label>
                <input
                  type="text"
                  className="referral-form-field"
                  name="crime_reference"
                  value={formState.crime_reference}
                  onChange={handleChange}
                  placeholder="Enter crime reference"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-4">
              <div className="flex flex-col mr-5 w-full">
                <label className="referral-form-label">Region</label>
                <input
                  type="text"
                  className="referral-form-field"
                  name="crime_location_region"
                  value={formState.crime_location_region}
                  onChange={handleChange}
                  placeholder="Enter region"
                />
              </div>
              <div className="flex flex-col w-full">
                <label className="referral-form-label">Zone</label>
                <input
                  type="text"
                  className="referral-form-field"
                  name="crime_location_zone"
                  value={formState.crime_location_zone}
                  onChange={handleChange}
                  placeholder="Enter zone"
                />
              </div>
              <div className="flex flex-col w-full">
                <label className="referral-form-label">Woreda</label>
                <input
                  type="text"
                  className="referral-form-field"
                  name="crime_location_woreda"
                  value={formState.crime_location_woreda}
                  onChange={handleChange}
                  placeholder="Enter woreda"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4 mt-4">
              <div className="flex flex-col mr-5 w-full">
                <label className="referral-form-label">Kebele</label>
                <input
                  type="text"
                  className="referral-form-field"
                  name="crime_location_town"
                  value={formState.crime_location_town}
                  onChange={handleChange}
                  placeholder="Enter kebele"
                />
              </div>
              <div className="flex flex-col w-full">
                <label className="referral-form-label">House Number</label>
                <input
                  type="text"
                  className="referral-form-field"
                  name="crime_house_number"
                  value={formState.crime_house_number}
                  onChange={handleChange}
                  placeholder="1234"
                />
              </div>
              <div className="flex flex-col w-full">
                <label className="referral-form-label">Special Place</label>
                <input
                  type="text"
                  className="referral-form-field"
                  name="special_place"
                  value={formState.crime_special_place}
                  onChange={handleChange}
                  placeholder="Enter special place"
                />
              </div>
            </div>
            <div className="flex justify-end mt-6">
              <button type="submit" className="referral-form-button text-white">
                Register
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AssetRegister;
